import { notFound } from "@tanstack/react-router";
import { OLD_SURGERY_MAP } from "@/lib/old-url-redirects";
import { surgeries, specialties, findTreatmentBySlug } from "@/lib/healic-data";
import { SurgeryPage, type SurgeryLoaderData } from "@/routes/surgeries.$slug";
import {
  TreatmentPage,
  buildContent,
  type TreatmentLoaderData,
} from "@/routes/treatments.$slug";
import { SpecialtyPage, type SpecialtyLoaderData } from "@/routes/specialties.$slug";

export type LegacyPayload =
  | { kind: "surgery"; data: SurgeryLoaderData; title: string; description: string }
  | { kind: "treatment"; data: TreatmentLoaderData; title: string; description: string }
  | { kind: "specialty"; data: SpecialtyLoaderData; title: string; description: string };

/** Resolve an old healic.in path (e.g. "general-surgeries/piles") to renderable payload. */
export function loadLegacyContent(oldKey: string): LegacyPayload {
  const target = OLD_SURGERY_MAP[oldKey];
  if (!target) throw notFound();

  if (target.kind === "surgery") {
    const surgery = surgeries.find((s) => s.slug === target.slug);
    if (!surgery) throw notFound();
    const specialty = specialties.find((sp) => sp.slug === surgery.specialtySlug);
    const related = surgeries
      .filter((s) => s.specialtySlug === surgery.specialtySlug && s.slug !== surgery.slug)
      .slice(0, 4);
    return {
      kind: "surgery",
      data: { surgery, specialty, related },
      title: `${surgery.name} — Cost, Best Doctors & Recovery | Healic`,
      description: surgery.short,
    };
  }

  if (target.kind === "treatment") {
    const treatment = findTreatmentBySlug(target.slug);
    if (!treatment) throw notFound();
    const specialty = specialties.find((sp) => sp.slug === treatment.specialtySlug)!;
    const related = specialty.treatments
      .filter((t: string) => t !== treatment.name)
      .slice(0, 4);
    const content = buildContent(treatment.name, specialty.name, treatment.slug);
    return {
      kind: "treatment",
      data: { treatment, specialty, related, content },
      title: `${treatment.name} — Cost, Best Doctors & Recovery | Healic`,
      description: content.short,
    };
  }

  // specialty
  const specialty = specialties.find((sp) => sp.slug === target.slug);
  if (!specialty) throw notFound();
  return {
    kind: "specialty",
    data: { specialty },
    title: `${specialty.name} — Treatments, Doctors & Cost | Healic`,
    description: specialty.short,
  };
}

export function LegacyContent({ payload }: { payload: LegacyPayload }) {
  if (payload.kind === "surgery") return <SurgeryPage data={payload.data} />;
  if (payload.kind === "treatment") return <TreatmentPage data={payload.data} />;
  return <SpecialtyPage data={payload.data} />;
}

const ORIGIN = "https://healic.in";

/** Build TanStack head() output for a legacy URL page. */
export function legacyHead(oldPath: string, payload: LegacyPayload) {
  const url = `${ORIGIN}${oldPath}`;
  return {
    meta: [
      { title: payload.title },
      { name: "description", content: payload.description },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      { property: "og:title", content: payload.title },
      { property: "og:description", content: payload.description },
      { property: "og:url", content: url },
      { property: "og:type", content: "article" },
      { property: "og:locale", content: "en_IN" },
      { property: "og:site_name", content: "Healic Multispeciality Hospital" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: payload.title },
      { name: "twitter:description", content: payload.description },
    ],
    links: [{ rel: "canonical", href: url }],
  };
}
